import { observer } from 'mobx-react-lite';
import { RefObject, useState } from 'react';
import Select, { IDropdownItem } from '../../../common/select/select.component';
import { HeaderSettingsPopupContainer } from './header-settings-popup.styles';
import { FlexContainer } from '../../../common/flex-container/flex-container.styles';
import Button from '../../../common/button/button.component';
import { Measure } from '../../../utils/parcel-measurements.ts/parcel-measurements-utils';
import { CountriesResponse } from '../../../models/countriesModel';
import { useRegionSettings } from './use-region-settings-hook';
import { useCloseOnOutsideClick } from '../../../hooks/use-close-outside-click.hook';
import { useCloseOnEscape } from '../../../hooks/use-close-on-escape.hook';

interface Props {
  setIsOpened: (isOpened: boolean) => void;
  triggerRef?: RefObject<HTMLElement>;
}

const HeaderSettingsPopup = ({ setIsOpened, triggerRef }: Props) => {
  const {
    t,
    country,
    language,
    currency,
    measures,
    setLanguage,
    setCurrency,
    setMeasures,
    handleCountryChange,
    handleSave,
    countryItems,
    languageItems,
    currencyItems,
    measureItems,
    isGuest,
    isSaving,
    getCountryLocalizedTitle,
  } = useRegionSettings();

  const [countryOpen, setCountryOpen] = useState<boolean>(false);
  const [languageOpen, setLanguageOpen] = useState<boolean>(false);
  const [currencyOpen, setCurrencyOpen] = useState<boolean>(false);
  const [measuresOpen, setMeasuresOpen] = useState<boolean>(false);

  const { popupRef } = useCloseOnOutsideClick(
    () => setIsOpened(false),
    triggerRef ? [triggerRef] : []
  );
  useCloseOnEscape(() => setIsOpened(false));

  return (
    <HeaderSettingsPopupContainer
      ref={popupRef}
      onClick={(e) => e.stopPropagation()}>
      <h3>{t('region_settings')}</h3>

      <FlexContainer
        $gap='12px'
        $justify='flex-start'
        $align='flex-start'
        $direction='column'>
        <Select
          id='country'
          title={
            <label htmlFor='country'>{t('settings_location_country')}</label>
          }
          items={countryItems}
          value={
            country
              ? {
                  value: country,
                  label: getCountryLocalizedTitle(country, true, language),
                }
              : undefined
          }
          isOpened={countryOpen}
          setIsOpened={setCountryOpen}
          onChange={(item: IDropdownItem<CountriesResponse>) =>
            handleCountryChange(item)
          }
          placeholder='Location country'
          inputAppearence='button'
          shouldMatchWidthOfInput
          renderDropdownInPortal={false}
        />

        <Select
          id='language'
          title={<label htmlFor='language'>{t('language')}</label>}
          items={languageItems}
          value={languageItems.find((i) => i.value === language)}
          isOpened={languageOpen}
          setIsOpened={setLanguageOpen}
          onChange={(item: IDropdownItem<string>) => setLanguage(item.value)}
          placeholder='Language'
          inputAppearence='button'
          shouldMatchWidthOfInput
          renderDropdownInPortal={false}
        />

        <Select
          id='currency'
          title={<label htmlFor='currency'>{t('currency')}</label>}
          items={currencyItems}
          value={currencyItems.find((i) => i.value === currency)}
          isOpened={currencyOpen}
          setIsOpened={setCurrencyOpen}
          onChange={(item: IDropdownItem<string>) => setCurrency(item.value)}
          placeholder='Currency'
          inputAppearence='button'
          shouldMatchWidthOfInput
          renderDropdownInPortal={false}
          isDisabled={isGuest}
        />

        <Select
          id='measures'
          title={<label htmlFor='measures'>{t('measures')}</label>}
          items={measureItems}
          value={measureItems.find((i) => Number(i.value) === measures)}
          isOpened={measuresOpen}
          setIsOpened={setMeasuresOpen}
          onChange={(item: IDropdownItem<string>) =>
            setMeasures(Number(item.value) as Measure)
          }
          placeholder='Measures'
          inputAppearence='button'
          shouldMatchWidthOfInput
          renderDropdownInPortal={false}
          isDisabled={isGuest}
        />
      </FlexContainer>

      <Button
        isLoading={isSaving}
        onClick={async () => {
          await handleSave();
          setIsOpened(false);
        }}>
        {t('save')}
      </Button>
    </HeaderSettingsPopupContainer>
  );
};

export default observer(HeaderSettingsPopup);
